import { useState } from "react";
import Toolbar from "@/components/Toolbar";
import { AnimatePresence } from "framer-motion";
import ChatMessage from "@/components/help-center/ChatMessage";
import FeedbackButtons from "@/components/help-center/FeedbackButtons";
import ProcessingIndicator from "@/components/help-center/ProcessingIndicator";
import ChatInput from "@/components/help-center/ChatInput";

type Message = {
  role: "assistant" | "user";
  content: string;
};

type ProcessingState = "thinking" | "fetching" | "writing" | null;

const HelpCenter = () => {
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Hey, how can I help?" }
  ]);
  const [input, setInput] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [processing, setProcessing] = useState<ProcessingState>(null);

  const simulateResponse = async () => {
    setProcessing("thinking");
    await new Promise(resolve => setTimeout(resolve, 1000));
    setMessages(prev => [...prev, { role: "assistant", content: "Give me a second and I'll check!" }]);
    
    setProcessing("fetching");
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setProcessing("writing");
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setMessages(prev => [...prev, { 
      role: "assistant", 
      content: `
        Just checked, your invoice "1234" with the name "John Doe" is owed $100 and should be paid by 1st of march the latest. You can view the invoice details here: 
        <a href="/mock-invoice-1234.pdf" class="text-blue-400 hover:text-blue-500 inline-flex items-center gap-1"><FileText className="w-4 h-4" />View Invoice PDF</a>
        <div class="mt-4">
          <img 
            src="/lovable-uploads/dbcc9406-7490-4136-bdf5-ee2842e973f1.png" 
            alt="Invoice Preview" 
            class="rounded-lg shadow-lg max-w-sm mx-auto"
          />
        </div>
      `
    }]);
    
    setProcessing(null);
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setMessages(prev => [...prev, { 
      role: "assistant", 
      content: "Did that answer your question?"
    }]);
    
    setShowFeedback(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    setMessages(prev => [...prev, { role: "user", content: input.trim() }]);
    setInput("");
    await simulateResponse();
  };

  const handleFeedback = (isPositive: boolean) => {
    setMessages(prev => [...prev, { 
      role: "user", 
      content: isPositive ? "Yes, thank you!" : "No, I need more help."
    }]);
    setShowFeedback(false);
    if (!isPositive) {
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "I apologize for not fully addressing your concern. Could you please provide more details about what you'd like to know?"
        }]);
      }, 1000);
    } else {
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Great! Let me know if you need anything else."
        }]);
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Toolbar />
      <div className="max-w-6xl mx-auto px-4 pt-32 pb-8">
        <div className="rounded-lg border border-white/10 bg-black/50 backdrop-blur-lg">
          <div className="h-[60vh] overflow-y-auto p-6 space-y-6">
            <AnimatePresence>
              {messages.map((message, index) => (
                <ChatMessage key={index} {...message} />
              ))}
              <ProcessingIndicator state={processing} />
            </AnimatePresence>

            {showFeedback && (
              <FeedbackButtons onFeedback={handleFeedback} />
            )}
          </div>
          
          <ChatInput 
            input={input}
            setInput={setInput}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;